import { useCallback, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import type { WorkSuggestion, WorkSuggestionWithLocalId } from '../../types';

export const useSuggestions = (): {
	fullSuggestionList: WorkSuggestionWithLocalId[];
	hideSuggestion: (id: string) => void;
	reset: () => void;
	suggestions: WorkSuggestionWithLocalId[];
	updateSuggestions: (newSuggestionList: WorkSuggestion[]) => void;
} => {
	const [hiddenSuggestionIds, setHiddenSuggestionIds] = useState<Set<string>>(new Set());
	const [fullSuggestionList, setFullSuggestionList] = useState<WorkSuggestionWithLocalId[]>([]);
	// used to avoid calling `setSuggestions` too many times during streaming - could break devtools
	const streamedIssueCount = useRef(0);

	const suggestions = useMemo(
		() => fullSuggestionList.filter((suggestion) => !hiddenSuggestionIds.has(suggestion.localId)),
		[fullSuggestionList, hiddenSuggestionIds],
	);

	const hideSuggestion = useCallback((id: string): void => {
		setHiddenSuggestionIds((set) => new Set(set).add(id));
	}, []);

	const reset = useCallback((): void => {
		setFullSuggestionList([]);
		setHiddenSuggestionIds(new Set());
		streamedIssueCount.current = 0;
	}, []);

	const addSuggestion = useCallback((suggestion: WorkSuggestion) => {
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		setFullSuggestionList((suggestions) => [...suggestions, { ...suggestion, localId: uuid() }]);
	}, []);

	const updateSuggestions = useCallback(
		(newSuggestionList: WorkSuggestion[]): void => {
			while (streamedIssueCount.current < newSuggestionList.length) {
				addSuggestion(newSuggestionList[streamedIssueCount.current]);
				streamedIssueCount.current++;
			}
		},
		[addSuggestion],
	);

	return {
		fullSuggestionList,
		suggestions,
		hideSuggestion,
		updateSuggestions,
		reset,
	};
};
